import { useState, useEffect } from 'react';
import { 
  ApplicationAPI, 
  Application, 
  CreateApplicationData, 
  UpdateApplicationData,
  ApplicationSecurityMetrics,
  ApplicationRiskAssessment,
  FindApplicationsOptions ,
  Pagination,
  ApplicationFilterOptions,
  VulnerabilityDistribution,
  LicenseDistribution
} from '../api';

export function useApplications(options?: FindApplicationsOptions) {
  const [data, setData] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>();

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await ApplicationAPI.getAll(options);
      
      if (response.code === '200' && response.data) {
         setData(response.data as any);

         setPagination({
           totalItems: response.totalNoOfItem || 0,
           page: response.page || 1,
           perPage: response.perPage || 10
         });
      } else {
        setError(response.message || 'Failed to fetch applications');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [options?.workgroupId]);

  return { 
    data, 
    loading, 
    error, 
    pagination,
    refetch: fetchApplications 
  };
}

export function useApplication(id: number) {
  const [data, setData] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await ApplicationAPI.getById(id);

      if (response.code === '200' && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to fetch application');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchApplication();
    }
  }, [id]);

  return { data, loading, error, refetch: fetchApplication };
}

export function useApplicationList(workgroupId: number) {
  const [data, setData] = useState<Application[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await ApplicationAPI.getList();

      if (response.code === '200' && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to fetch application');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workgroupId) {
      fetchApplication();
    }
  }, [workgroupId]);

  return { data, loading, error, refetch: fetchApplication };
}

export function useApplicationProjects(id: number) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProjects() {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await ApplicationAPI.getProjects(id);
        
        if (response.code === '200' && response.data) {
          setData(response.data);
        } else {
          setError(response.message || 'Failed to fetch application projects');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchProjects();
  }, [id]);

  return { data, loading, error };
}

export function useApplicationMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createApplication = async (data: CreateApplicationData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApplicationAPI.create(data);
      
      if (response.code === '201' || response.code === '200') {
        return response.data;
      } else {
        setError(response.message || 'Failed to create application');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateApplication = async (id: number, data: UpdateApplicationData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApplicationAPI.update(id, data);
      
      if (response.code === '200') {
        return response.data;
      } else {
        setError(response.message || 'Failed to update application');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteApplication = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApplicationAPI.delete(id);
      
      if (response.code === '200') {
        return true;
      } else {
        setError(response.message || 'Failed to delete application');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    createApplication,
    updateApplication,
    deleteApplication,
    loading,
    error,
  };
  
}
